import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Phone, MapPin, Clock, Stethoscope, Heart, Activity, Users, ChevronDown, Star } from 'lucide-react';
import Navbar from './Navbar';
import Footer from './Footer';

const HERO_IMG = "/building.jpg";
const ABOUT_IMG = "https://images.pexels.com/photos/6787812/pexels-photo-6787812.jpeg?auto=compress&cs=tinysrgb&w=900&q=80";

const services = [
  {
    icon: Stethoscope,
    title: "Full-Spectrum Family Medicine",
    desc: "Comprehensive primary care for patients of all ages — from newborns to seniors — right here in Clarendon.",
    testid: "service-family-medicine"
  },
  {
    icon: Activity,
    title: "Minor Procedures",
    desc: "In-office minor surgical procedures and treatments so you can receive quality care close to home.",
    testid: "service-procedures"
  },
  {
    icon: Heart,
    title: "Hormone Replacement Therapy",
    desc: "Personalized HRT programs to help you feel your best at every stage of life.",
    testid: "service-hrt"
  },
  {
    icon: Users,
    title: "Licensed Counseling — Now Available",
    desc: "We're excited to welcome a licensed counselor to our team, now offering supportive mental wellness care for our community.",
    testid: "service-counseling",
    badge: "Now Available"
  }
];

const hours = [
  { day: "Monday", time: "8:00 AM – 5:00 PM" },
  { day: "Tuesday", time: "8:00 AM – 5:00 PM" },
  { day: "Wednesday", time: "8:00 AM – 5:00 PM" },
  { day: "Thursday", time: "8:00 AM – 5:00 PM" },
  { day: "Friday", time: "8:00 AM – 12:00 PM" },
  { day: "Saturday", time: "Closed" },
  { day: "Sunday", time: "Closed" },
];

export default function HomePage() {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Clarendon Family Medicine | Family Practice in Clarendon, TX</title>
        <meta name="description" content="Clarendon Family Medicine — family practice, pediatrics, and preventive care in Clarendon, TX. Accepting new patients." />
        <meta property="og:title" content="Clarendon Family Medicine | Family Practice in Clarendon, TX" />
        <meta property="og:description" content="Clarendon Family Medicine — family practice, pediatrics, and preventive care in Clarendon, TX. Accepting new patients." />
        <meta property="og:url" content="https://www.clarendon.clinic/" />
        <meta property="og:image" content="https://www.clarendon.clinic/og-image.jpg" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Clarendon Family Medicine | Family Practice in Clarendon, TX" />
        <meta name="twitter:description" content="Clarendon Family Medicine — family practice, pediatrics, and preventive care in Clarendon, TX. Accepting new patients." />
        <meta name="twitter:image" content="https://www.clarendon.clinic/og-image.jpg" />
        <link rel="canonical" href="https://www.clarendon.clinic/" />
      </Helmet>
      <Navbar />

      {/* ── HERO ── */}
      <section
        id="hero"
        data-testid="hero-section"
        className="relative min-h-screen flex items-center overflow-hidden pt-20"
      >
        {/* Background — clean gradient, no faded image */}
        <div className="absolute inset-0 z-0 bg-gradient-to-br from-white via-sky-50/80 to-sky-100/60" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-20 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left */}
          <div className="animate-fade-in-up">
            <p className="text-sky-600 font-semibold text-sm uppercase tracking-widest mb-4">
              Rural Health Clinic — Clarendon, Texas
            </p>
            <h1 className="text-5xl sm:text-6xl font-bold text-sky-900 leading-tight mb-6" style={{fontFamily: 'Outfit, sans-serif'}}>
              Caring for<br />
              <span className="text-sky-600">Your Family</span><br />
              Close to Home
            </h1>
            <p className="text-lg text-sky-700 leading-relaxed mb-8 max-w-lg">
              Clarendon Family Medicine provides warm, compassionate, full-spectrum care for patients of all ages — children, adults, and seniors — right here in the Texas Panhandle.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="tel:8068743531"
                data-testid="hero-call-btn"
                className="flex items-center justify-center gap-2 bg-sky-600 hover:bg-sky-700 text-white px-8 py-4 rounded-full font-semibold text-base transition-all duration-200 hover:shadow-xl hover:shadow-sky-200 hover:-translate-y-0.5"
              >
                <Phone size={18} />
                Call (806) 874-3531
              </a>
              <button
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                data-testid="hero-contact-btn"
                className="flex items-center justify-center gap-2 border-2 border-sky-200 hover:border-sky-400 text-sky-700 px-8 py-4 rounded-full font-semibold text-base transition-all duration-200 hover:bg-sky-50"
              >
                Find Us
                <MapPin size={16} />
              </button>
            </div>
          </div>

          {/* Right — building image card */}
          <div className="hidden lg:block animate-fade-in-up delay-300">
            <div className="relative">
              <div className="absolute -inset-4 bg-sky-200/30 rounded-[2.5rem] blur-2xl" />
              <img
                src={HERO_IMG}
                alt="Clarendon Family Medicine clinic building"
                className="relative w-full rounded-3xl object-cover shadow-2xl shadow-sky-200/50"
                style={{ height: '520px', objectPosition: 'center' }}
              />
              {/* Floating badge */}
              <div className="absolute bottom-6 left-6 bg-white/95 backdrop-blur-sm rounded-2xl px-5 py-4 shadow-lg border border-sky-100">
                <div className="flex items-center gap-3">
                  <div className="bg-sky-100 rounded-full p-2">
                    <Star size={16} className="text-sky-600 fill-sky-300" />
                  </div>
                  <div>
                    <p className="text-sky-900 font-bold text-sm">Your Local Clinic</p>
                    <p className="text-sky-500 text-xs">One Medical Center Drive · Clarendon, TX</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 animate-bounce">
          <ChevronDown size={24} className="text-sky-400" />
        </div>
      </section>

      {/* ── SERVICES ── */}
      <section
        id="services"
        data-testid="services-section"
        className="py-24 bg-sky-50"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-sky-500 font-semibold text-sm uppercase tracking-widest mb-3">What We Offer</p>
            <h2 className="text-4xl sm:text-5xl font-bold text-sky-900 mb-4" style={{fontFamily: 'Outfit, sans-serif'}}>
              Our Services
            </h2>
            <p className="text-sky-700 text-lg max-w-2xl mx-auto">
              We're proud to provide a broad range of healthcare services designed to meet the needs of our entire community.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((s) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.testid}
                  data-testid={s.testid}
                  className="bg-white border border-sky-100 rounded-3xl p-8 shadow-sm hover:shadow-md hover:shadow-sky-100 hover:-translate-y-1 transition-all duration-300 relative"
                >
                  {s.badge && (
                    <span data-testid={`${s.testid}-badge`} className="absolute top-4 right-4 bg-emerald-100 text-emerald-700 text-xs font-semibold px-2.5 py-1 rounded-full">
                      {s.badge}
                    </span>
                  )}
                  <div className="bg-sky-100 rounded-2xl w-14 h-14 flex items-center justify-center mb-5">
                    <Icon size={26} className="text-sky-600" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-sky-900 font-bold text-lg mb-3 leading-tight" style={{fontFamily: 'Outfit, sans-serif'}}>{s.title}</h3>
                  <p className="text-sky-700 text-sm leading-relaxed">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── ABOUT ── */}
      <section
        id="about"
        data-testid="about-section"
        className="py-24 bg-white"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Image */}
          <div className="relative order-2 lg:order-1">
            <div className="absolute -inset-4 bg-sky-100/50 rounded-[2.5rem] blur-2xl" />
            <img
              src={ABOUT_IMG}
              alt="Happy elderly couple enjoying time together outdoors"
              className="relative w-full rounded-3xl object-cover shadow-xl shadow-sky-100"
              style={{ height: '480px' }}
            />
          </div>

          {/* Text */}
          <div className="order-1 lg:order-2">
            <p className="text-sky-500 font-semibold text-sm uppercase tracking-widest mb-3">About Our Practice</p>
            <h2 className="text-4xl sm:text-5xl font-bold text-sky-900 mb-6" style={{fontFamily: 'Outfit, sans-serif'}}>
              Your Neighbors.<br />
              <span className="text-sky-600">Your Doctors.</span>
            </h2>
            <div className="space-y-4 text-sky-700 text-base leading-relaxed">
              <p>
                Clarendon Family Medicine is a proud rural health clinic nestled in the heart of Clarendon, Texas. We believe that living in a small town should never mean settling for less when it comes to your healthcare.
              </p>
              <p>
                We practice <strong className="text-sky-800">full-spectrum family medicine</strong>, meaning we care for patients of all ages — from newborns and children to adults and seniors. We delight in building lasting relationships with our patients and their families.
              </p>
              <p>
                Our team specializes in creating a <strong className="text-sky-800">warm, caring environment</strong> where you feel known and valued — not just a number. We perform minor procedures in-office, offer hormone replacement therapy, and are continuously growing to serve you better.
              </p>
            </div>

            <div className="mt-8 grid grid-cols-2 gap-4">
              <div data-testid="about-stat-1" className="bg-sky-50 rounded-2xl p-5 border border-sky-100">
                <p className="text-3xl font-bold text-sky-600 mb-1" style={{fontFamily: 'Outfit, sans-serif'}}>All Ages</p>
                <p className="text-sky-700 text-sm">Children &amp; Adults Welcome</p>
              </div>
              <div data-testid="about-stat-2" className="bg-sky-50 rounded-2xl p-5 border border-sky-100">
                <p className="text-3xl font-bold text-sky-600 mb-1" style={{fontFamily: 'Outfit, sans-serif'}}>Full Scope</p>
                <p className="text-sky-700 text-sm">Comprehensive Family Care</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── OFFICE HOURS ── */}
      <section
        id="hours"
        data-testid="hours-section"
        className="py-24 bg-sky-600"
      >
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <div className="bg-white rounded-3xl p-10 shadow-2xl shadow-sky-900/20">
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="bg-sky-100 rounded-full p-3">
                <Clock size={24} className="text-sky-600" />
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-sky-900" style={{fontFamily: 'Outfit, sans-serif'}}>Office Hours</h2>
            </div>
            <div className="max-w-sm mx-auto space-y-3">
              {hours.map((h) => (
                <div
                  key={h.day}
                  data-testid={`hours-${h.day.toLowerCase()}`}
                  className={`flex justify-between items-center py-3 border-b border-sky-100 last:border-0 ${
                    h.time === 'Closed' ? 'opacity-50' : ''
                  }`}
                >
                  <span className="font-semibold text-sky-800 text-sm">{h.day}</span>
                  <span className={`text-sm font-medium ${h.time === 'Closed' ? 'text-sky-400' : 'text-sky-600'}`}>
                    {h.time}
                  </span>
                </div>
              ))}
            </div>
            <p className="text-sky-500 text-sm mt-6">
              For urgent matters, please call <a href="tel:8068743531" className="text-sky-600 font-semibold hover:underline">(806) 874-3531</a>
            </p>
          </div>
        </div>
      </section>

      {/* ── CONTACT ── */}
      <section
        id="contact"
        data-testid="contact-section"
        className="py-24 bg-white"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-sky-500 font-semibold text-sm uppercase tracking-widest mb-3">Get In Touch</p>
            <h2 className="text-4xl sm:text-5xl font-bold text-sky-900 mb-4" style={{fontFamily: 'Outfit, sans-serif'}}>
              Come See Us
            </h2>
            <p className="text-sky-700 text-lg max-w-xl mx-auto">
              We'd love to be your family's healthcare home. Reach out or stop by — we're here for you.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {/* Address */}
            <div data-testid="contact-address" className="bg-sky-50 border border-sky-100 rounded-3xl p-8 text-center hover:shadow-md transition-all duration-300">
              <div className="bg-white rounded-2xl w-14 h-14 flex items-center justify-center mx-auto mb-5 shadow-sm border border-sky-100">
                <MapPin size={24} className="text-sky-600" strokeWidth={1.5} />
              </div>
              <h3 className="font-bold text-sky-900 text-lg mb-2" style={{fontFamily: 'Outfit, sans-serif'}}>Our Location</h3>
              <p className="text-sky-700 text-sm leading-relaxed">
                One Medical Center Drive<br />
                Clarendon, TX 79226
              </p>
              <a
                href="https://maps.google.com/?q=One+Medical+Center+Drive+Clarendon+TX+79226"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="get-directions-btn"
                className="inline-block mt-4 text-sky-600 hover:text-sky-800 text-sm font-semibold transition-colors"
              >
                Get Directions &rarr;
              </a>
            </div>

            {/* Phone */}
            <div data-testid="contact-phone" className="bg-sky-600 rounded-3xl p-8 text-center hover:bg-sky-700 transition-all duration-300">
              <div className="bg-white/20 rounded-2xl w-14 h-14 flex items-center justify-center mx-auto mb-5">
                <Phone size={24} className="text-white" strokeWidth={1.5} />
              </div>
              <h3 className="font-bold text-white text-lg mb-2" style={{fontFamily: 'Outfit, sans-serif'}}>Call Us</h3>
              <a
                href="tel:8068743531"
                data-testid="contact-call-link"
                className="text-sky-100 text-xl font-bold hover:text-white transition-colors"
              >
                (806) 874-3531
              </a>
              <p className="text-sky-200 text-xs mt-2">Mon–Thu 8AM–5PM · Fri 8AM–Noon</p>
            </div>

            {/* Hours quick ref */}
            <div data-testid="contact-hours" className="bg-sky-50 border border-sky-100 rounded-3xl p-8 text-center hover:shadow-md transition-all duration-300">
              <div className="bg-white rounded-2xl w-14 h-14 flex items-center justify-center mx-auto mb-5 shadow-sm border border-sky-100">
                <Clock size={24} className="text-sky-600" strokeWidth={1.5} />
              </div>
              <h3 className="font-bold text-sky-900 text-lg mb-2" style={{fontFamily: 'Outfit, sans-serif'}}>Office Hours</h3>
              <p className="text-sky-700 text-sm">
                Mon – Thu<br />
                <strong className="text-sky-800">8:00 AM – 5:00 PM</strong>
              </p>
              <p className="text-sky-700 text-sm mt-2">
                Friday<br />
                <strong className="text-sky-800">8:00 AM – 12:00 PM</strong>
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
